import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../hooks/CustomHook';
import config from '../config/config';

const useInView = (options) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisible(true);
                observer.disconnect();
            }
        }, options);
        observer.observe(el);
        return () => observer.disconnect();
    }, [options]);

    return [ref, visible];
};

const Reveal = ({ children, className = '', delay = 0 }) => {
    const [ref, visible] = useInView({ threshold: 0.1, rootMargin: '0px 0px -32px 0px' });
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
            }}
        >
            {children}
        </div>
    );
};

const ZuyuApp = () => {
    useDocTitle('江西族迹 | 族语APP');
    const { featureBanner, features } = config.appPage;

    return (
        <>
            <NavBar />
            <main className="zuyu-app-page bg-[#f4f7fb] text-gray-800">
                <header className="relative overflow-hidden pt-28 pb-14 md:pt-36 md:pb-20">
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                'radial-gradient(ellipse 75% 55% at 15% 10%, #dbeafe 0%, transparent 55%), radial-gradient(ellipse 60% 45% at 100% 0%, #ccfbf1 0%, transparent 45%), linear-gradient(180deg, #eff6ff 0%, #f4f7fb 100%)',
                        }}
                    />
                    <div className="relative max-w-3xl mx-auto px-6 text-center">
                        <p className="text-sm tracking-[0.28em] text-blue-700/80 font-semibold mb-5 animate-[zuyuFade_0.8s_ease_both]">
                            族语APP
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold leading-tight text-slate-900 mb-5 animate-[zuyuFade_0.9s_ease_0.08s_both]">
                            族语APP
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-800 font-medium leading-snug animate-[zuyuFade_0.9s_ease_0.16s_both]">
                            留住家庭记忆，连接血缘亲情
                        </p>
                    </div>
                </header>

                <div className="max-w-5xl mx-auto px-6 pb-10">
                    <Reveal>
                        <section className="max-w-3xl mx-auto mb-16 md:mb-24">
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
                                族语APP是什么？
                            </h2>
                            <div className="h-1 w-14 bg-blue-600 rounded-full mb-8" />
                            <div className="space-y-5 text-lg text-slate-600 leading-[1.85]">
                                <p>
                                    族语是江西族迹信息技术有限公司开发的一款以家庭记忆保存和亲情连接为核心的社交APP。它不同于普通社交软件，也不同于单纯的云存储工具，而是围绕“家庭”这一长期关系场景设计，通过数字化方式帮助用户记录家庭成员关系、成长经历、人生故事以及重要情感时刻。
                                </p>
                                <p>
                                    族语希望解决的是一个长期存在的问题：如何让一个家庭的故事被保存，并能够被下一代继续看见。
                                </p>
                                <p>
                                    通过数字技术，族语为家庭建立一个专属的记忆空间，让照片、视频、文字和人生经历不再只是零散的数据，而成为一份可以持续完善的家庭档案。
                                </p>
                                <p>
                                    目前已上线Android、IOS、HarmonyOS三大平台，我们以“用数字技术连系至亲血脉”为核心理念，致力于让每一个普通家庭的记忆与情感，在数字世界里成长为一棵枝繁叶茂、永远不倒的“家庭生命树”。
                                </p>
                            </div>
                        </section>
                    </Reveal>
                </div>

                {/* 功能区上方横图 */}
                <Reveal>
                    <div className="w-full mb-14 md:mb-20">
                        <img
                            src={featureBanner}
                            alt="族语APP功能预览"
                            loading="lazy"
                            decoding="async"
                            className="w-full max-h-[420px] object-cover object-center"
                        />
                    </div>
                </Reveal>

                <div className="max-w-5xl mx-auto px-6 pb-20 md:pb-28">
                    <Reveal>
                        <div className="text-center mb-14 md:mb-20">
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                                族语APP有哪些功能？
                            </h2>
                            <div className="h-1 w-14 bg-blue-600 rounded-full mx-auto" />
                        </div>
                    </Reveal>

                    <div className="space-y-20 md:space-y-28">
                        {features.map((item, index) => {
                            const reverse = index % 2 === 1;
                            const hasImage = Boolean(item.image);
                            return (
                                <Reveal key={item.id} delay={40}>
                                    <section
                                        className={`flex flex-col gap-8 md:gap-12 md:items-center ${
                                            hasImage
                                                ? reverse
                                                    ? 'md:flex-row-reverse'
                                                    : 'md:flex-row'
                                                : 'max-w-3xl mx-auto'
                                        }`}
                                    >
                                        {hasImage && (
                                            <div className="w-full md:w-[44%] flex-shrink-0">
                                                <img
                                                    src={item.image}
                                                    alt={item.title}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-full h-auto max-h-[520px] object-contain mx-auto"
                                                />
                                            </div>
                                        )}
                                        <div className={hasImage ? 'w-full md:w-[56%]' : 'w-full'}>
                                            <p className="text-blue-600 font-semibold tracking-wider text-sm mb-2">
                                                {item.id}
                                            </p>
                                            <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                                                {item.title}
                                            </h3>
                                            <p className="text-blue-800/90 font-medium mb-5">
                                                {item.subtitle}
                                            </p>
                                            <div className="space-y-4 text-slate-600 leading-[1.8] text-base md:text-lg">
                                                {item.paragraphs.map((p, i) => (
                                                    <p key={i}>{p}</p>
                                                ))}
                                            </div>
                                        </div>
                                    </section>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={60}>
                        <div className="mt-20 md:mt-28 flex flex-col sm:flex-row justify-center gap-4">
                            <Link
                                to="/download"
                                className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors duration-300"
                            >
                                立即下载
                            </Link>
                            <Link
                                to="/about"
                                className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl border-2 border-blue-600 text-blue-700 hover:bg-blue-50 font-medium transition-colors duration-300"
                            >
                                关于我们
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </main>
            <Footer />
            <style>{`
                @keyframes zuyuFade {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
};

export default ZuyuApp;
